import React, { forwardRef, useCallback, useRef, useState } from 'react'

const VARIANT_CLASSES = {
  primary: 'btn-scfo-primary',
  ghost: 'btn-scfo-ghost',
  danger: 'btn-scfo-danger',
}

const ScfoButton = forwardRef(function ScfoButton(
  {
    variant = 'primary',
    loading = false,
    async: asyncClick = false,
    cooldownMs = 500,
    disableOnLoading = true,
    disabled = false,
    onClick,
    type = 'button',
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const [internalLoading, setInternalLoading] = useState(false)
  const lastClickRef = useRef(0)
  const isExecutingRef = useRef(false)

  const isLoading = Boolean(loading || internalLoading)
  const isDisabled = Boolean(disabled) || (disableOnLoading && isLoading)

  const handleClick = useCallback(
    async (e) => {
      if (!onClick) return
      if (!asyncClick) {
        onClick(e)
        return
      }
      if (isExecutingRef.current) return
      const now = Date.now()
      if (now - lastClickRef.current < cooldownMs) return
      if (disabled || loading) return
      lastClickRef.current = now
      isExecutingRef.current = true
      setInternalLoading(true)
      try {
        await onClick(e)
      } finally {
        isExecutingRef.current = false
        setInternalLoading(false)
      }
    },
    [onClick, asyncClick, disabled, loading, cooldownMs],
  )

  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary
  const classes = ['btn-scfo', variantClass, className].filter(Boolean).join(' ')

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      onClick={handleClick}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? <span className="scfo-button__spinner" aria-hidden /> : null}
      {children}
    </button>
  )
})

export default ScfoButton
